<template>
  <aside class="sidebar" :class="{ 'is-collapsed': collapsed }">
    <div class="sidebar__brand">
      <div class="sidebar__logo">云</div>
      <div v-if="!collapsed" class="sidebar__brand-text">
        <strong>交发云咖</strong>
        <span>运营管理平台</span>
      </div>
    </div>

    <button class="sidebar__collapse" type="button" aria-label="折叠菜单" @click="appStore.toggleSidebar">
      <el-icon :class="{ 'is-rotate': collapsed }"><ArrowLeft /></el-icon>
    </button>

    <nav class="sidebar__menu">
      <section v-for="group in visibleOperationMenus" :key="group.title" class="sidebar__group">
        <button class="sidebar__group-button" type="button" :title="collapsed ? group.title : undefined" @click="toggleGroup(group.title)">
          <span>
            <el-icon size="20">
              <component :is="iconMap[group.icon]" />
            </el-icon>
            <template v-if="!collapsed">{{ group.title }}</template>
          </span>
          <el-icon v-if="!collapsed" :class="{ 'is-expanded': expandedGroup === group.title }"><ArrowDown /></el-icon>
        </button>

        <div v-if="!collapsed" class="sidebar__items" :class="{ 'is-open': expandedGroup === group.title }">
          <div class="sidebar__items-inner">
            <RouterLink
              v-for="item in group.items"
              :key="item.path"
              class="sidebar__item"
              :class="{ 'is-active': route.path === item.path }"
              :to="item.path"
            >
              <span>{{ item.name }}</span>
              <em v-if="item.badge">{{ item.badge }}</em>
            </RouterLink>
          </div>
        </div>
      </section>
    </nav>

    <div v-if="!collapsed" class="sidebar__version" :title="`构建版本 ${buildId}`">版本 {{ buildId }}</div>
  </aside>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { Component } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowDown,
  ArrowLeft,
  Bell,
  CoffeeCup,
  DataAnalysis,
  Medal,
  Money,
  Present,
  Setting,
  Shop,
  ShoppingBag,
  TrendCharts,
  Van,
  User,
} from '@element-plus/icons-vue'
import { operationMenus, type OperationIcon } from '@/data/operationDesign'
import { useAppStore } from '@/stores/app'
import { usePermissionStore } from '@/stores/permission'
import { BUILD_ID } from '@/utils/build-info'

defineProps<{ collapsed: boolean }>()

// 界面上显示线上跑的是哪一次构建：用户与运维都能自查，不必翻发布日志。
// 取值链路见 src/utils/build-info.ts（与 dist/version.json 的 buildId 同源）。
const buildId = BUILD_ID

const route = useRoute()
const appStore = useAppStore()
const permissionStore = usePermissionStore()
const visibleRoutePaths = computed(() => new Set(permissionStore.menus.map((menu) => {
  const path = menu.path.startsWith('/') ? menu.path : `/${menu.path}`
  return path.replace(/\/+$/, '') || '/'
})))
const visibleOperationMenus = computed(() =>
  operationMenus
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => visibleRoutePaths.value.has(item.path)),
    }))
    .filter((group) => group.items.length > 0),
)
const activeGroup = computed(() =>
  visibleOperationMenus.value.find((group) => group.items.some((item) => item.path === route.path))?.title ??
  visibleOperationMenus.value[0]?.title ??
  '',
)
const expandedGroup = ref(activeGroup.value)

const iconMap: Record<OperationIcon, Component> = {
  dashboard: DataAnalysis,
  store: Shop,
  coffee: CoffeeCup,
  truck: Van,
  users: User,
  award: Medal,
  gift: Present,
  chart: TrendCharts,
  wallet: Money,
  bell: Bell,
  settings: Setting,
  bag: ShoppingBag,
  drone: Van,
}

watch(activeGroup, (group) => {
  if (group) expandedGroup.value = group
})

const toggleGroup = (title: string) => {
  if (appStore.sidebarCollapsed) {
    appStore.sidebarCollapsed = false
    expandedGroup.value = title
    return
  }
  expandedGroup.value = expandedGroup.value === title ? '' : title
}
</script>

<style scoped lang="scss">
.sidebar { width: 224px; min-height: 100vh; position: relative; flex: 0 0 auto; display: flex; flex-direction: column; background: #fff; border-right: 1px solid var(--jf-color-border); transition: width .3s; }
.sidebar.is-collapsed { width: 72px; }
.sidebar__brand { height: 64px; padding: 0 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; justify-content: center; gap: 12px; white-space: nowrap; }
.sidebar__logo { width: 32px; height: 32px; border-radius: 8px; flex: 0 0 auto; display: grid; place-items: center; background: var(--jf-color-primary); color: #fff; font-weight: 800; }
.sidebar__brand-text { display: flex; flex-direction: column; line-height: 1.2; }
.sidebar__brand-text strong { color: var(--jf-color-text-primary); font-size: 16px; }
.sidebar__brand-text span { margin-top: 3px; color: var(--jf-color-text-secondary); font-size: 10px; }
.sidebar__collapse { width: 24px; height: 24px; position: absolute; top: 80px; right: -12px; z-index: 5; border: 1px solid var(--jf-color-border); border-radius: 999px; display: grid; place-items: center; background: #fff; color: var(--jf-color-text-secondary); box-shadow: 0 2px 8px rgba(37, 40, 43, .08); cursor: pointer; }
.sidebar__collapse:hover { color: var(--jf-color-primary-dark); }
.sidebar__collapse .el-icon { transition: transform .2s; }
.sidebar__collapse .is-rotate { transform: rotate(180deg); }
.sidebar__menu { flex: 1; overflow-y: auto; padding: 16px 0; scrollbar-width: none; }
.sidebar__menu::-webkit-scrollbar { display: none; }
.sidebar__group { margin-bottom: 8px; }
.sidebar__group-button { width: 100%; min-height: 36px; padding: 0 24px; border: 0; display: flex; align-items: center; justify-content: space-between; background: transparent; color: var(--jf-color-text-secondary); font-size: 14px; font-weight: 700; cursor: pointer; transition: color .2s; }
.is-collapsed .sidebar__group-button { justify-content: center; padding: 12px 0; }
.sidebar__group-button:hover { color: var(--jf-color-primary-dark); }
.sidebar__group-button span { display: inline-flex; align-items: center; gap: 8px; }
.sidebar__group-button .el-icon:last-child { transition: transform .2s; }
.sidebar__group-button .is-expanded { transform: rotate(180deg); }
.sidebar__items { display: grid; grid-template-rows: 0fr; min-height: 0; margin: 0; overflow: hidden; opacity: 0; transition: grid-template-rows .3s ease, opacity .2s ease, margin .2s ease; }
.sidebar__items-inner { min-height: 0; overflow: hidden; display: flex; flex-direction: column; gap: 4px; }
.sidebar__items.is-open { grid-template-rows: 1fr; margin: 4px 0 14px; opacity: 1; }
.sidebar__item { min-height: 36px; position: relative; margin: 0 12px; padding: 0 12px 0 42px; border-radius: 8px; display: flex; align-items: center; color: var(--jf-color-text-primary); font-size: 14px; transition: background .2s, color .2s; }
.sidebar__item::before { content: ''; width: 1px; height: 16px; position: absolute; left: 24px; top: 10px; border-radius: 999px; background: rgba(115, 119, 123, .22); }
.sidebar__item:hover { background: rgba(252, 250, 246, .9); color: var(--jf-color-primary-dark); }
.sidebar__item.is-active { background: rgba(242, 169, 80, .1); color: #bf7022; font-weight: 700; }
.sidebar__item.is-active::after { content: ''; width: 4px; height: 16px; position: absolute; left: 0; top: 50%; border-radius: 0 999px 999px 0; background: var(--jf-color-primary-dark); transform: translateY(-50%); }
.sidebar__item em { min-width: 20px; height: 18px; margin-left: auto; padding: 0 6px; border-radius: 999px; display: inline-grid; place-items: center; background: var(--jf-color-error); color: #fff; font-size: 10px; font-style: normal; }
.sidebar__version { flex: 0 0 auto; padding: 10px 16px 14px; border-top: 1px solid var(--jf-color-border); color: var(--jf-color-text-tertiary, var(--jf-color-text-secondary)); font-size: 11px; line-height: 1.4; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
